import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["as", "children", "isTruncated", "useTooltip", "type", "bold", "color", "withDivider", "isHighlighted", "classes", "overrides", "className"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { forwardRef, useRef, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import defaultTheme from '../../../utils/styles/defaultTheme';
import { getOverrides, useClasses } from '../../../utils/overrides';
import { createUseStyles } from '../../../utils/styles';
import Tooltip from '../../utils/Tooltip';
import styles from './styles';
var useStyles = createUseStyles(styles, 'Text');
var Text = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var _classnames;
  var as = _ref.as,
    children = _ref.children,
    isTruncated = _ref.isTruncated,
    useTooltip = _ref.useTooltip,
    type = _ref.type,
    bold = _ref.bold,
    color = _ref.color,
    withDivider = _ref.withDivider,
    isHighlighted = _ref.isHighlighted,
    classesProp = _ref.classes,
    overridesProp = _ref.overrides,
    classNameProp = _ref.className,
    props = _objectWithoutProperties(_ref, _excluded);
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    tooltipContent = _useState2[0],
    setTooltipContent = _useState2[1];
  var defaultRef = useRef(null);
  var classes = useClasses(useStyles, classesProp);
  //Overrides
  var rootClassName = classnames(classes.root, classNameProp, classes[type], (_classnames = {}, _defineProperty(_classnames, classes.bold, bold), _defineProperty(_classnames, classes.truncated, isTruncated), _defineProperty(_classnames, classes.divider, withDivider), _defineProperty(_classnames, classes.highlighted, isHighlighted), _classnames));
  var override = getOverrides(overridesProp, Text.overrides);
  var rootProps = useMemo(function () {
    return _objectSpread(_objectSpread({}, props), override.root);
  }, [override.root, props]);
  var _ref2 = /*#__PURE__*/React.createElement("span", null, children);
  useEffect(function () {
    var _finalRef$current, _finalRef$current2;
    var finalRef = ref || defaultRef;
    if (useTooltip && ((_finalRef$current = finalRef.current) === null || _finalRef$current === void 0 ? void 0 : _finalRef$current.offsetWidth) < ((_finalRef$current2 = finalRef.current) === null || _finalRef$current2 === void 0 ? void 0 : _finalRef$current2.scrollWidth)) setTooltipContent(_ref2);
  }, [children, useTooltip, ref]);
  var style = useMemo(function () {
    return color && defaultTheme.colors[color] ? {
      color: defaultTheme.colors[color]
    } : {};
  }, [color]);
  var textContainer = useMemo(function () {
    var Tag = as || 'span';
    return /*#__PURE__*/React.createElement(Tag, _extends({
      ref: ref || defaultRef,
      className: rootClassName,
      style: style
    }, rootProps), children);
  }, [as, children, ref, rootClassName, rootProps, style]);
  if (useTooltip) return /*#__PURE__*/React.createElement(Tooltip, {
    placement: "top",
    content: tooltipContent
  }, textContainer);
  return textContainer;
});
Text.overrides = ['root', 'Loader'];
Text.defaultProps = {
  as: 'span',
  type: 'body',
  className: '',
  overrides: {},
  isTruncated: false
};
process.env.NODE_ENV !== "production" ? Text.propTypes = {
  as: PropTypes.oneOf(['div', 'span', 'strong', 'em', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'pre', 'blockquote', 'label', 'li']),
  className: PropTypes.string,
  type: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'subtitle1', 'subtitle', 'body1', 'body', 'button', 'caption', 'captionMedium', 'badges', 'overline']),
  color: PropTypes.oneOf(['neutralBase', 'neutral100', 'neutral150', 'neutral200', 'neutral300', 'neutral400', 'neutral500', 'neutral600', 'neutral700', 'neutral800', 'neutral900', 'red100', 'red200', 'red400', 'red500', 'red600', 'red700', 'red900', 'orange100', 'orange200', 'orange400', 'orange500', 'orange600', 'orange700', 'orange900', 'green100', 'green200', 'green400', 'green500', 'green600', 'green700', 'green900', 'blue100', 'blue200', 'blue400', 'blue500', 'blue600', 'blue700', 'blue900', 'purple100', 'purple200', 'purple400', 'purple500', 'purple600', 'purple700', 'purple900', 'aqua100', 'aqua200', 'aqua400', 'aqua500', 'aqua600', 'aqua700', 'aqua900', 'yellow100', 'yellow200', 'yellow400', 'yellow500', 'yellow600', 'yellow700', 'yellow900', 'turquoise100', 'turquoise200', 'turquoise400', 'turquoise500', 'turquoise600', 'turquoise700', 'turquoise900']),
  overrides: PropTypes.object,
  children: PropTypes.node,
  isTruncated: PropTypes.bool,
  bold: PropTypes.bool,
  useTooltip: PropTypes.bool,
  withDivider: PropTypes.bool,
  isHighlighted: PropTypes.bool
} : void 0;
export default /*#__PURE__*/React.memo(Text);